import React, { useMemo, useState } from 'react';
import { LayoutChangeEvent, StyleProp, View, ViewStyle } from 'react-native';
import { encodeCode39 } from '../../utils/barcode';

interface BarcodeProps {
  /** Value to encode — e.g. the student identification number. */
  value: string;
  height?: number;
  color?: string;
  /** Width of a narrow bar; wide bars are three times this. */
  moduleWidth?: number;
  style?: StyleProp<ViewStyle>;
}

/**
 * Renders a Code 39 barcode at true Code 39 proportions: bars are drawn at a
 * fixed module width so the code stays as long as its content needs and no
 * longer. It only shrinks below that when the space available is too narrow.
 */
export const Barcode: React.FC<BarcodeProps> = ({
  value,
  height = 40,
  color = '#0F172A',
  moduleWidth = 2,
  style,
}) => {
  const barcode = useMemo(() => encodeCode39(value), [value]);
  const [availableWidth, setAvailableWidth] = useState(0);

  if (!barcode) {
    return null;
  }

  const unitWidth =
    availableWidth > 0
      ? Math.min(moduleWidth, availableWidth / barcode.totalUnits)
      : moduleWidth;

  const onLayout = (event: LayoutChangeEvent) => {
    setAvailableWidth(event.nativeEvent.layout.width);
  };

  return (
    <View onLayout={onLayout} style={[{ alignSelf: 'stretch', overflow: 'hidden' }, style]}>
      <View style={{ flexDirection: 'row', height }}>
        {barcode.modules.map((module, index) => (
          <View
            key={index}
            style={{
              width: module.units * unitWidth,
              backgroundColor: module.bar ? color : 'transparent',
            }}
          />
        ))}
      </View>
    </View>
  );
};
